import { useState, useCallback } from 'react';
import { CandidateCard } from '@/components/swipe/CandidateCard';
import { SwipeControls } from '@/components/swipe/SwipeControls';
import { MatchModal } from '@/components/swipe/MatchModal';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import { mockProfiles } from '@/data/mockProfiles';

// Number of cards to render in the stack
const STACK_SIZE = 3;

export default function CandidatesPage() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showMatch, setShowMatch] = useState(false);
  const [likedCandidates, setLikedCandidates] = useState<string[]>([]);

  const currentCandidate = mockProfiles[currentIndex];
  const hasMoreCandidates = currentIndex < mockProfiles.length;

  const handleSwipeLeft = useCallback(() => {
    if (!hasMoreCandidates) return;
    setCurrentIndex((prev) => prev + 1);
  }, [hasMoreCandidates]);

  const handleSwipeRight = useCallback(() => {
    if (!hasMoreCandidates) return;
    setLikedCandidates((prev) => [...prev, currentCandidate.id]);
    
    const isMatch = Math.random() > 0.6;
    if (isMatch) {
      setShowMatch(true);
    }
    
    setCurrentIndex((prev) => prev + 1);
  }, [hasMoreCandidates, currentCandidate]);

  const { handlers, getCardStyle, getOverlayOpacity, isDragging, isAnimatingOut } = useSwipeGesture({
    onSwipeLeft: handleSwipeLeft,
    onSwipeRight: handleSwipeRight,
    threshold: 100,
  });

  // Get the cards to render in the stack
  const stackCards = mockProfiles.slice(currentIndex, currentIndex + STACK_SIZE);

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* Card Container - Responsive height */}
      <div className="flex-1 min-h-0 flex items-start justify-center px-4 pt-1 relative">
        {stackCards.length > 0 ? (
          <div className="w-full max-w-md flex-shrink-0 relative" style={{ height: 'clamp(400px, calc(100vh - 240px), 550px)' }}>
            {/* Render cards behind (scaled down, blurred, no gesture handling) */}
            {stackCards.slice(1).reverse().map((candidate, reversedIndex) => {
              const stackIndex = stackCards.length - 1 - reversedIndex;
              return (
                <div
                  key={candidate.id}
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    zIndex: STACK_SIZE - stackIndex,
                    transform: 'scale(0.5)',
                    filter: 'blur(4px)',
                  }}
                >
                  <CandidateCard candidate={candidate} onSwipe={() => {}} />
                </div>
              );
            })}

            {/* Top card with gesture handling */}
            <div
              key={stackCards[0].id}
              className="absolute inset-0"
              style={{ zIndex: STACK_SIZE }}
            >
              <div
                className="relative h-full"
                {...handlers}
                style={{
                  ...getCardStyle(),
                  animation: !isDragging && !isAnimatingOut ? 'cardEnter 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' : 'none',
                }}
              >
                <CandidateCard 
                  candidate={stackCards[0]} 
                  onSwipe={(direction) => {
                    if (direction === 'left') handleSwipeLeft();
                    else handleSwipeRight();
                  }}
                  isDragging={isDragging}
                />

                {/* Swipe overlay indicators */}
                <div
                  className="absolute top-20 left-8 pointer-events-none"
                  style={{ opacity: getOverlayOpacity('left') }}
                >
                  <div className="px-6 py-3 rounded-2xl bg-[#2d3142] border-4 border-white rotate-[-20deg]">
                    <span className="text-2xl font-bold text-white">NOPE</span>
                  </div>
                </div>
                <div
                  className="absolute top-20 right-8 pointer-events-none"
                  style={{ opacity: getOverlayOpacity('right') }}
                >
                  <div className="px-6 py-3 rounded-2xl bg-[#FF3B5C] border-4 border-white rotate-[20deg]">
                    <span className="text-2xl font-bold text-white">LIKE</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2 text-white">Ingen flere kandidater</h2>
            <p className="text-gray-400">Du har sett {likedCandidates.length} kandidater!</p>
            <button
              onClick={() => {
                setCurrentIndex(0);
                setLikedCandidates([]);
              }}
              className="mt-4 px-6 py-3 bg-[#FF3B5C] text-white rounded-full font-semibold hover:opacity-90"
            >
              Start på nytt
            </button>
          </div>
        )}
      </div>

      {/* Swipe Controls */}
      {stackCards.length > 0 && (
        <div className="flex-shrink-0 py-2 px-4 relative z-10 bg-[#1a1d2e]">
          <SwipeControls
            onPass={handleSwipeLeft}
            onLike={handleSwipeRight}
          />
        </div>
      )}

      {showMatch && currentCandidate && (
        <MatchModal
          job={{
            title: currentCandidate.title,
            company: currentCandidate.name,
          }}
          onClose={() => setShowMatch(false)}
          onStartChat={() => {
            setShowMatch(false);
          }}
        />
      )}
    </div>
  );
}

